import { Card } from '@devfolioco/genesis';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import DocumentTitle from 'react-document-title';
import React from 'react';
import * as Sentry from '@sentry/browser';

import { ACTION_STATUS as STATUS } from '@constants';
import { getDevfolioCookie } from 'helpers/cookie';
import { history } from 'helpers/history';
import { storeItem, getItemFromStorage, removeItemFromStorage } from 'helpers/localStorage';
// import * as LogRocket from 'helpers/logRocket';
import * as quizActions from 'actions/quiz';

import Header from './components/Header';
import Options from './components/Options';
import Submit from './components/Submit';
import TimerWithTip from './components/TimerWithTip';
import TitleWithTip from './components/TitleWithTip';
import Skeleton from './components/Skeleton';

class Quiz extends React.Component {
  /**
   * Reload the page.
   *
   * @static
   * @memberof Quiz
   */
  static reload = () => {
    if (typeof window === 'object') {
      window.location.reload();
    }
  };

  /**
   * Go to the start quiz page for a given quizId, and username.
   *
   * @static
   * @memberof Quiz
   */
  static goBackToStartQuiz = quizId => {
    history.replace({
      pathname: `/quiz/start`,
      state: { quizId },
    });
  };

  /**
   * Route to the end quiz page.
   *
   * @static
   * @memberof Quiz
   */
  static routeToEndQuiz = result => {
    history.replace({
      pathname: `/quiz/end`,
      state: { fromQuiz: true, result },
    });
  };

  // >
  // SAMPLE QUIZ HELPER METHODS

  /**
   * Update the sample quiz localstorage object.
   *
   * @static
   * @memberof Quiz
   */
  static updateSampleQuizStore = store => {
    storeItem('devfolio-sample-quiz', store);
  };

  /**
   * Remove the local storage quiz object, and route to start quiz page.
   *
   * @static
   * @memberof Quiz
   */
  static clearQuizAndRoute = quizId => {
    removeItemFromStorage('devfolio-sample-quiz');
    Quiz.goBackToStartQuiz(quizId);
  };

  /**
   * Update the current sample quiz object with new current pointer,
   * and reload the page.
   *
   * @static
   * @memberof Quiz
   */
  static updateStorageAndReload = () => {
    const sampleQuiz = getItemFromStorage('devfolio-sample-quiz');

    if (sampleQuiz) {
      // Set current to -1 to indicate that the quiz has already been taken once.
      const updatedSampleQuiz = {
        ...sampleQuiz,
        current: sampleQuiz.current + 1 >= 5 ? -1 : sampleQuiz.current + 1,
      };
      Quiz.updateSampleQuizStore(updatedSampleQuiz);
    }

    window.location.reload();
  };

  /* Start time for the sample quiz timer */
  sampleQuizStartTime = Date.now();

  constructor(props) {
    super(props);

    this.state = {
      /** State for the controlled option selection */
      selectedOptionId: -1,

      /** Updates on each timer tick, tracking the time taken to respond to a question */
      timeTaken: -1,

      /** quizId of the current quiz */
      quizId: null,

      /** whether page is loading */
      loading: true,

      /**  Current sample quiz quesiton */
      currentSampleQuestion: 0,
    };

    if (typeof window === 'object') {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    }
  }

  /**
   * When the component is mounting, and is in sample mode, we check and
   * make any updates to the local storage, and route to the start if required.
   *
   * @memberof Quiz
   */

  // eslint-disable-next-line camelcase, react/sort-comp
  UNSAFE_componentWillMount() {
    const {
      currentQuiz: { result },
      sample,
      location,
    } = this.props;

    const sampleQuiz = getItemFromStorage('devfolio-sample-quiz');

    if (sample) {
      try {
        const { quizId, userId } = getDevfolioCookie();

        // In the sample mode, if the localstorage object with same quiz id is found
        // we'll check if it has been already taken by the same user.
        if (sampleQuiz && sampleQuiz.userId === userId && sampleQuiz.quizId === quizId) {
          if (sampleQuiz.current === -1 || sampleQuiz.current > 4) {
            // When the sample quiz has been taken once:
            // 1. we will remove it from the storage
            // 2. and route to start.

            Quiz.clearQuizAndRoute(quizId);
          } else if (
            location.hasOwnProperty('state') &&
            location.state &&
            location.state.hasOwnProperty('fromStartQuiz')
          ) {
            // When the user wishes to retake the quiz, or take a different
            // sample quiz (with a different quizId), we'll set inital state.
            Quiz.updateSampleQuizStore({ quizId, userId, current: 0 });
          }
        } else {
          // When no item in the localstorage is found, we create one.
          Quiz.updateSampleQuizStore({ quizId, userId, current: 0 });
        }
      } catch (error) {
        history.replace('/badges');
      }
    }

    // When result is received via the server, we will route to
    // the end page.
    if (typeof result === 'string') {
      this.updatePage();
    }
  }

  componentDidMount() {
    try {
      const { quizId } = getDevfolioCookie();
      const sampleQuiz = getItemFromStorage('devfolio-sample-quiz');

      // LogRocket.init();

      this.setState(Object.assign({ quizId }, sampleQuiz && { currentSampleQuestion: sampleQuiz.current || 0 }));
    } catch (error) {
      history.replace('/badges');
    }

    this.setState({ loading: false });
  }

  /**
   * React to the status changes. Reload the page, on success, and
   * in sample mode, update the storage, before reloading.
   *
   * @memberof Quiz
   */
  componentDidUpdate(prevProps) {
    const { submitResponseStatus, submitSkipStatus, submitTimeoutStatus, submitSampleResponseStatus } = this.props;

    // Check the statuses of the submit responses including,
    // skip and timeout, and update the page.
    if (prevProps.submitResponseStatus !== submitResponseStatus) {
      if (submitResponseStatus === STATUS.SUCCESS) {
        this.updatePage();
      }
    }

    if (prevProps.submitSkipStatus !== submitSkipStatus) {
      if (submitSkipStatus === STATUS.SUCCESS) {
        this.updatePage();
      }
    }

    if (prevProps.submitTimeoutStatus !== submitTimeoutStatus) {
      if (submitTimeoutStatus === STATUS.SUCCESS) {
        this.updatePage();
      }
    }

    // Check if the sample mode response is successful. If yes, we'll
    // update the storage and reload the page.
    const { sample } = this.props;

    if (sample && prevProps.submitSampleResponseStatus !== submitSampleResponseStatus) {
      if (submitSampleResponseStatus === STATUS.SUCCESS) {
        Quiz.updateStorageAndReload();
      }
    }
  }

  componentWillUnmount() {
    if (typeof window === 'object') {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    }
  }

  /**
   * If we have the result, which means that the quiz has ended, in that
   * case, move to the end quiz page, else reload the page.
   *
   * @memberof Quiz
   */
  updatePage = () => {
    const {
      currentQuiz: { result },
    } = this.props;

    if (result) {
      Quiz.routeToEndQuiz(result);
    } else {
      Quiz.reload();
    }
  };

  /**
   * When the timer runs out, dispatch the timeout action.
   *
   * @memberof Quiz
   */
  skip = () => {
    const { sample, submitTimeout, submitSampleResponse } = this.props;

    if (sample) {
      submitSampleResponse({ timeout: true });
    } else {
      submitTimeout();
    }
  };

  /**
   * Triggered once the user hits submit. Submit the response, along with the
   * time taken, or dispatch a skip action, if the 5th option is selected.
   *
   * @memberof Quiz
   */
  submitAnswer = event => {
    event.preventDefault();

    const { sample, submitSkip, submitResponse, submitSampleResponse, currentQuestion, username } = this.props;
    const { selectedOptionId, timeTaken } = this.state;

    const answerOptions = {
      timeTaken,
      optionId: selectedOptionId,
    };

    if (sample) {
      if (selectedOptionId === 'skip') {
        submitSampleResponse({ skip: true });
      } else {
        submitSampleResponse(answerOptions);
      }

      return;
    }

    const { estimatedTime } = currentQuestion;

    if (timeTaken < 0 || timeTaken > estimatedTime) {
      Sentry.withScope(scope => {
        scope.setTag('caught-in', 'Quiz');
        scope.setUser({ username });
        scope.setExtra('current-question', JSON.stringify(currentQuestion));

        if (timeTaken < 0) {
          Sentry.captureMessage(`[submitAnswer]: timeTaken is negative. timeTaken: ${timeTaken}`);
        }

        if (timeTaken > estimatedTime) {
          Sentry.captureMessage(`[submitAnswer]: timeTaken is greater than the estimatedTime. timeTaken: ${timeTaken}`);
        }
      });

      answerOptions.timeTaken = estimatedTime;
    }

    if (selectedOptionId === 'skip') {
      submitSkip();
    } else {
      submitResponse(answerOptions);
    }
  };

  /**
   * Update the state, as per selected option.
   *
   * @memberof Quiz
   */
  updateSelectedOption = newoptionId => {
    this.setState({ selectedOptionId: newoptionId });
  };

  /**
   * Update the time taken state on every tick.
   *
   * @memberof Quiz
   */
  updateTimeTaken = timeTaken => {
    this.setState({ timeTaken });
  };

  render() {
    const { sample, sampleQuiz, currentQuestion, csrfToken } = this.props;
    const { selectedOptionId, timeTaken, quizId, loading, currentSampleQuestion } = this.state;
    const showTip = sample && currentSampleQuestion < 2;
    let displayQuestion = currentQuestion;

    if (sample) {
      const storeSampleQuiz = getItemFromStorage('devfolio-sample-quiz');

      if (storeSampleQuiz !== null) {
        displayQuestion = sampleQuiz.questions[storeSampleQuiz.current];
      } else {
        return null;
      }
    }

    const {
      currentTime,
      estimatedTime,
      question,
      options,
      number,
      total,
      codeSnippet,
      questionEndTime,
      codeSnippetLanguage,
    } = displayQuestion;

    return (
      <DocumentTitle title="Quiz | Devfolio">
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            width: '100%',
            userSelect: 'none',
          }}
        >
          <Header quizId={quizId} sample={sample} />

          <form onSubmit={this.submitAnswer}>
            <Card>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: 36,
                }}
              >
                {loading && <Skeleton />}

                {!loading && (
                  <>
                    <div style={{ maxWidth: 800, width: '100%', minWidth: 800 }}>
                      <TitleWithTip
                        currentTime={currentTime}
                        sample={showTip}
                        number={number}
                        total={total}
                        text={question}
                        codeSnippet={codeSnippet}
                        codeSnippetLanguage={codeSnippetLanguage}
                      />

                      <Options onClick={this.updateSelectedOption} options={options} selected={selectedOptionId} />
                    </div>

                    <TimerWithTip
                      currentTime={currentTime}
                      estimatedTime={estimatedTime}
                      onTick={this.updateTimeTaken}
                      onTimeout={this.skip}
                      questionEndTime={sample ? this.sampleQuizStartTime + estimatedTime * 1000 : questionEndTime}
                      sample={showTip}
                      quizId={quizId}
                    />
                  </>
                )}
              </div>
            </Card>

            {!loading && (
              <div style={{ margin: '64px 0', marginLeft: 192 }}>
                <input type="hidden" name="_csrf" value={csrfToken} />

                <Submit sample={showTip} disabled={timeTaken === -1 || selectedOptionId === -1} />
              </div>
            )}
          </form>
        </div>
      </DocumentTitle>
    );
  }
}

const mapStateToProps = ({ quiz, authentication }) => ({
  csrfToken: quiz.csrfToken,
  currentQuestion: quiz.currentQuestion,
  currentQuiz: quiz.currentQuiz,
  sampleQuiz: quiz.sampleQuiz,
  submitResponseStatus: quiz.status.submitResponse,
  submitSampleResponseStatus: quiz.status.submitSampleResponse,
  submitSkipStatus: quiz.status.submitSkip,
  submitTimeoutStatus: quiz.status.submitTimeout,
  username: authentication.username,
  userId: authentication.uuid,
});

const mapDispatchToProps = dispatch => ({
  submitTimeout: () => {
    dispatch(quizActions.submitTimeout());
  },
  submitSkip: () => {
    dispatch(quizActions.submitSkip());
  },
  submitResponse: answer => {
    dispatch(quizActions.submitResponse(answer));
  },
  submitSampleResponse: answer => {
    dispatch(quizActions.submitSampleResponse(answer));
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Quiz));
